import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { getAdminProfile, updateAdminProfile } from "../../slices/thunks";
import avatar from "../../assets/images/users/avatar-1.jpg";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import ImageCropper from "../../Components/Common/ImageCropper";
import { User } from "react-feather";

const UserProfile = () => {
  const dispatch = useDispatch();

  const selectProfileState = (state) => state;

  const profileSelector = createSelector(selectProfileState, (state) => ({
    user: state.Aprofile.user,
    loading: state.Aprofile.loading,
    error: state.Aprofile.error,
  }));

  const { user, loading, error } = useSelector(profileSelector);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    address: "",
    signature: "",
    signatureFile: null,
  });

  const [originalData, setOriginalData] = useState({});
  const [showSignatureCropper, setShowSignatureCropper] = useState(false);
  const [signatureSrc, setSignatureSrc] = useState(null);

  useEffect(() => {
    const authUser = sessionStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      dispatch(getAdminProfile(parsed.data.id));
    }
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      const data = {
        name: user?.name || "",
        email: user?.email || "",
        mobileNo: user?.mobileNo || "",
        address: user?.address || "",

        profilePic: user?.profilePic || "",
        signature: user?.signaturePic || "",
      };

      setFormData(data);
      setOriginalData(data);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignatureCrop = (blob) => {
    const file = new File([blob], "signature.jpg", {
      type: "image/jpeg",
    });

    const previewUrl = URL.createObjectURL(blob);

    setFormData((prev) => ({
      ...prev,
      signature: previewUrl,
      signatureFile: file,
    }));

    setShowSignatureCropper(false);
  };

  useEffect(() => {
    console.log("showSignatureCropper:", showSignatureCropper);
  }, [showSignatureCropper]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const changes = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== originalData[key]) {
        changes[key] = formData[key];
      }
    });

    if (Object.keys(changes).length === 0) {
      toast.info("No changes made.");
      return;
    }

    const authUser = sessionStorage.getItem("authUser");

    if (authUser) {
      const parsed = JSON.parse(authUser);

      const queryPayload = {
        id: parsed.data.id,
        name: formData.name,
        email: formData.email,
        mobileNo: formData.mobileNo,
        address: formData.address,
      };

      const imageFormData = new FormData();
      if (formData.profilePicFile) {
        imageFormData.append("adminProfilePic", formData.profilePicFile);
      }

      if (formData.signatureFile) {
        imageFormData.append("signaturePic", formData.signatureFile);
      }

      dispatch(updateAdminProfile(queryPayload, imageFormData))
        .then((res) => {
          dispatch(getAdminProfile(parsed.data.id));
        })
        .catch((err) => {
          toast.error("Update failed. Please try again.");
        });
    }
  };

  document.title = "Admin Profile";

  return (
    <div className="page-content ">
      <Container fluid>
        <Row>
          <Col lg="6" className="mx-auto">
            {error && <Alert color="danger">{error}</Alert>}
            <Card>
              <CardBody className="p-4">
                <div className="border rounded p-4 mb-4 text-center">
                  <div className="position-relative d-inline-block mb-2">
                    {formData.profilePic ? (
                      <img
                        src={formData.profilePic}
                        alt="avatar"
                        className="rounded-circle img-thumbnail"
                        style={{
                          width: "140px",
                          height: "140px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-light border"
                        style={{ width: "140px", height: "140px" }}
                      >
                        <User size={56} className="text-muted" />
                      </div>
                    )}

                    <label
                      htmlFor="profilePicUpload"
                      className="position-absolute bottom-0 end-0 bg-primary rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                      }}
                    >
                      <i className="ri-camera-line text-white"></i>
                    </label>

                    <input
                      type="file"
                      id="profilePicUpload"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData((prev) => ({
                            ...prev,
                            profilePic: reader.result,
                            profilePicFile: file,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </div>

                  <h5 className="mb-0">{formData.name}</h5>
                  <small className="text-muted">{formData.email}</small>
                </div>

                <div className="border rounded p-4 mb-4">
                  <h5 className="mb-3">Profile Information</h5>

                  <Form id="profileForm" onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md="6">
                        <FormGroup>
                          <Label>Name</Label>
                          <Input
                            type="text"
                            name="name"
                            value={formData?.name}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            name="email"
                            value={formData?.email}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label>Mobile No</Label>
                          <Input
                            type="text"
                            name="mobileNo"
                            value={formData?.mobileNo}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <Label>Address</Label>
                          <Input
                            type="text"
                            name="address"
                            value={formData?.address}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </div>

                <div className="border rounded p-4">
                  <h5 className="mb-3">Signature</h5>

                  <div
                    className="border rounded p-3 text-center mb-3"
                    style={{ background: "rgb(233, 237, 237)" }}
                  >
                    {formData.signature ? (
                      <img
                        src={formData.signature}
                        alt="signature"
                        style={{
                          maxWidth: "50%",
                          height: "40px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <small className="text-muted">
                        No signature uploaded
                      </small>
                    )}
                  </div>

                  <Label
                    htmlFor="signatureUpload"
                    className="btn btn-outline-primary btn-sm"
                  >
                    Upload Signature
                  </Label>

                  <Input
                    type="file"
                    id="signatureUpload"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      e.target.value = "";
                      if (!file) return;

                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setSignatureSrc(reader.result);
                        setShowSignatureCropper(true);
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </div>
                <div className="text-center mt-4">
                  <Button
                    color="success"
                    className="px-4"
                    type="submit"
                    form="profileForm"
                  >
                    Update Profile
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showSignatureCropper && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          >
            <div
              className="bg-white p-3 rounded shadow"
              style={{ width: "420px" }}
            >
              <h6 className="mb-2">Crop Signature</h6>
              <ImageCropper
                imageSrc={signatureSrc}
                aspect={3 / 1}
                onCropComplete={handleSignatureCrop}
                onClose={() => setShowSignatureCropper(false)}
              />
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default UserProfile;
